/** @jsx jsx */
import { Fragment, useState } from 'react'
import { RadioButton, Loader } from '@livechat/design-system'
import { css, jsx } from '@emotion/core'
import PieChart from 'react-minimal-pie-chart'

import noAnswersIcon from '../assets/icons/no-asnwers.svg'
import MarkdownRenderer from './MarkdownRenderer'

const chartCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: auto;
  min-height: 400px;
  background-color: white;

  width: calc(100% - 40px);
  max-width: 500px;
  padding: 20px;
  padding-top: 25px;
  border-radius: 10px;
  box-shadow: rgb(228 228 228) 0px 1px 5px 0px;

  :nth-of-type(2n) {
    margin-right: auto;
  }

  :nth-of-type(2n + 1) {
    margin-left: auto;
  }

  @media (max-width: 980px) {
    margin: 0 !important;
  }

  .question {
    font-weight: 400;
    text-align: center;
    margin-bottom: 30px;
    font-size: 18px;
  }

  .pie {
    width: 170px;
  }

  .answers {
    margin-top: 20px;
  }

  .loader {
  }
`

const radioCss = (noAnswers, isBiggest) => css`
  max-width: 300px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;

  .answer {
    display: flex;
  }

  .text {
    font-weight: ${isBiggest ? 500 : 100};
    margin-top: 2px;
    color: ${noAnswers ? 'hsl(0, 0%, 70%)' : isBiggest ? '#3F8CFF' : '#424d59'};
  }

  .value {
    font-weight: ${isBiggest ? 500 : 100};
    margin-top: 2px;
    margin-left: 10px;
    color: ${noAnswers ? 'hsl(0, 0%, 70%)' : isBiggest ? '#3F8CFF' : '#424d59'};
  }
`

const SingleChoiceChart = ({
  field,
  fieldIndex,
  response,
  structure,
  isLoading,
  surveyId,
}) => {
  const [sliced, setSliced] = useState(true)
  const data = {}
  response
    .map((el) => el.response[fieldIndex])
    .forEach((el) => {
      if (data[el]) {
        data[el]++
      } else {
        data[el] = 1
      }
    })

  const colors = ['#3F8CFF', '#85A3F3', '#AFC5FF', '#D3E0FF']
  let counter = 0
  const formattedData = []
  const objectArray = Object.entries(data)
  objectArray.forEach(([indexOfAnswer, numberOfPicks]) => {
    formattedData.push({
      title: structure[fieldIndex].answers[indexOfAnswer],
      value: numberOfPicks,
    })
  })

  formattedData.sort((a, b) => b.value - a.value)

  const formattedDataWithColor = formattedData.map((el, index) => {
    return {
      ...el,
      color:
        colors[
          formattedData[index].value > formattedData[index + 1]?.value
            ? counter++
            : counter
        ],
    }
  })

  const answers = structure[fieldIndex].answers
    .map((answer) => ({
      answer,
      value: formattedData.find((el) => el.title === answer)?.value || 0,
    }))
    .sort((a, b) => b.value - a.value)

  const formattedAnswers = sliced ? answers.slice(0, 3) : answers

  return (
    <div css={chartCss}>
      <div className="question">
        <MarkdownRenderer>{field.question}</MarkdownRenderer>
      </div>

      {isLoading ? (
        <Loader className="loader" size="large" />
      ) : formattedDataWithColor && formattedDataWithColor.length > 0 ? (
        <Fragment>
          <PieChart
            className="pie"
            data={formattedDataWithColor}
            animate={true}
            animationDuration={500}
            animationEasing="ease-out"
            lineWidth={20}
            paddingAngle={18}
            radius={50}
            rounded
            startAngle={0}
            viewBoxSize={[100, 100]}
            label={({ data, dataIndex }) =>
              `${data[dataIndex].percentage.toFixed(0)}%`
            }
            labelPosition={60}
            lengthAngle={360}
            labelStyle={{
              fontFamily: 'sans-serif',
              fontSize: '10px',
            }}
          />
          <div className="answers">
            {formattedAnswers.map((el, index) => {
              const printBiggestValue =
                index === 0
                  ? answers[0].value > (answers[1]?.value || 0)
                  : false
              return (
                <div
                  key={`${surveyId}-${fieldIndex}-single-choice-chart-${index}`}
                  css={radioCss(!el.value, printBiggestValue)}
                >
                  <div className="answer">
                    <RadioButton checked={printBiggestValue}>
                      <div />
                    </RadioButton>
                    <span className="text">{el.answer}</span>
                  </div>
                  <span className="value">({el.value})</span>
                </div>
              )
            })}
            {answers.length > 3 && sliced && (
              <span
                css={css`
                  display: inline-block;
                  margin: 10px 0 0 calc(50% - 35px);
                  color: rgb(67, 132, 245);
                  cursor: pointer;

                  :hover {
                    text-decoration: underline;
                  }
                `}
                onClick={() => setSliced(false)}
              >
                See more
              </span>
            )}
          </div>
        </Fragment>
      ) : (
        <span
          css={css`
            display: flex;
            align-items: center;
            color: hsl(0, 0%, 70%);
            font-weight: 100;

            > img {
              margin-right: 5px;
            }
          `}
        >
          <img src={noAnswersIcon} alt="no answers" />{' '}
          <span>No answers given</span>
        </span>
      )}
    </div>
  )
}

export default SingleChoiceChart
